/**
 * The login screen (`050-auth.md`). Outside the AppShell and outside RequireAuth.
 *
 * The two criteria this screen exists to satisfy (M1 #2 and #6):
 *
 *   - A wrong password shows an inline error and **does not clear the username
 *     field**. The username is controlled state that the failure path never touches.
 *   - Six failed logins in a row return 429 with `Retry-After`. The button disables
 *     and counts down. "Do not let the UI keep hammering a throttled endpoint."
 */
import { useEffect, useState, type FormEvent } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router';
import { ApiError } from '@/api/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useLogin, useSession } from '@/features/auth/useSession';
import { translate, useTranslation } from '@/lib/i18n';

/** The server never distinguishes "no such user" from "wrong password". Neither do we. */
function messageFor(error: unknown): string {
  if (!(error instanceof ApiError)) return translate('auth.login.error.network');

  switch (error.code) {
    case 'unauthenticated':
      return translate('auth.login.error.badCredentials');
    case 'validation_failed':
      return error.violations?.[0]?.message ?? translate('auth.login.error.validation');
    case 'rate_limited':
      return error.message;
    default:
      return error.message || translate('auth.login.error.generic');
  }
}

function formatCountdown(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  const rest = seconds % 60;
  return minutes > 0 ? `${String(minutes)}m ${String(rest)}s` : `${String(rest)}s`;
}

export function LoginPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { data: session } = useSession();
  const { t } = useTranslation();

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [lockedFor, setLockedFor] = useState(0);

  const { mutate, isPending, error, reset } = useLogin();

  // The 429 countdown. `Retry-After` is in seconds (050-auth.md §"The 429 response").
  useEffect(() => {
    if (lockedFor <= 0) return;
    const timer = window.setInterval(() => {
      setLockedFor((remaining) => Math.max(0, remaining - 1));
    }, 1000);
    return () => {
      window.clearInterval(timer);
    };
  }, [lockedFor]);

  // Already signed in (or signed in in another tab): don't show a form we don't need.
  if (session) {
    const next = params.get('next');
    return <Navigate to={next !== null && next.startsWith('/') ? next : '/'} replace />;
  }

  const locked = lockedFor > 0;

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (locked || isPending) return;

    mutate(
      { username, password },
      {
        onSuccess: () => {
          const next = params.get('next');
          void navigate(next !== null && next.startsWith('/') ? next : '/', { replace: true });
        },
        onError: (failure) => {
          // NOTE what is NOT here: `setUsername('')`. Criterion #2. The password is
          // cleared (it is wrong, and leaving it invites a second identical attempt
          // against a throttled endpoint); the username is not.
          setPassword('');
          if (failure instanceof ApiError && failure.status === 429) {
            setLockedFor(failure.retryAfter ?? 60);
          }
        },
      },
    );
  };

  return (
    <div className="flex h-full items-center justify-center p-6">
      <form onSubmit={onSubmit} className="w-full max-w-[300px] space-y-4" noValidate>
        <div className="space-y-1">
          <h1 className="text-lg font-semibold">{t('auth.login.appName')}</h1>
          <p className="text-[var(--muted-foreground)]">{t('auth.login.subtitle')}</p>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="username">{t('auth.login.username')}</Label>
          <Input
            id="username"
            name="username"
            autoComplete="username"
            autoFocus
            required
            value={username}
            onChange={(event) => {
              setUsername(event.target.value);
              if (error) reset();
            }}
            aria-invalid={error ? true : undefined}
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="password">{t('auth.login.password')}</Label>
          <Input
            id="password"
            name="password"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(event) => {
              setPassword(event.target.value);
              if (error) reset();
            }}
            aria-invalid={error ? true : undefined}
          />
        </div>

        {/* The throttle message is keyed off `locked`, NOT off `error`: editing a field
            clears the mutation error (so the "wrong password" message goes away as you
            fix it), and a lockout that disappeared the moment you touched the form would
            leave a disabled button with no explanation. */}
        {locked ? (
          <p role="alert" className="text-[var(--destructive)]">
            {t('auth.login.lockout', { countdown: formatCountdown(lockedFor) })}
          </p>
        ) : (
          error !== null && (
            <p role="alert" className="text-[var(--destructive)]">
              {messageFor(error)}
            </p>
          )
        )}

        <Button type="submit" className="w-full" disabled={locked || isPending}>
          {locked
            ? t('auth.login.submitLocked', { countdown: formatCountdown(lockedFor) })
            : isPending
              ? t('auth.login.submitting')
              : t('auth.login.submit')}
        </Button>
      </form>
    </div>
  );
}
